import { useEffect, useRef } from "react";
import * as THREE from "three";

// Network of discrete tetrahedral molecular units (central atom + 4 peripherals)
// connected by thinner inter-unit filaments. Diagonal rotation axis, glassy
// PBR bonds, dense bloom-style halos, faked DOF via aggressive exponential fog.
export default function Molecule3D() {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const width = mount.clientWidth;
        const height = mount.clientHeight;

        const scene = new THREE.Scene();
        // Strong exponential fog produces a depth-of-field-style falloff: front sharp, back dissolves
        scene.fog = new THREE.FogExp2(0xe8f5fc, 0.024);

        const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
        // Effective world-space bounding radius of the molecule cluster (incl. its
        // top-right anchor offset). Used to keep the molecule filling a consistent
        // proportion of the frame across any aspect ratio (phone/tablet/desktop),
        // instead of leaving large empty margins on wide screens.
        const CLUSTER_RADIUS = 6.0;
        const FRAME_FILL = 0.95;
        const fitCamera = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            const aspect = w / h;
            camera.aspect = aspect;
            const vFovRad = (camera.fov * Math.PI) / 180;
            const distance =
                aspect >= 1
                    ? CLUSTER_RADIUS / (FRAME_FILL * Math.tan(vFovRad / 2))
                    : CLUSTER_RADIUS / (FRAME_FILL * aspect * Math.tan(vFovRad / 2));
            camera.position.set(0, 0, distance);
            camera.updateProjectionMatrix();
        };
        fitCamera();

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 3));
        renderer.setSize(width, height);
        renderer.setClearColor(0x000000, 0);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.3;
        mount.appendChild(renderer.domElement);

        // --- Smoky volumetric backdrop (blue/purple wisps) ---
        const nebulaGeo = new THREE.SphereGeometry(55, 48, 48);
        const nebulaMat = new THREE.ShaderMaterial({
            side: THREE.BackSide,
            depthWrite: false,
            transparent: true,
            uniforms: { uTime: { value: 0 } },
            vertexShader: `
                varying vec3 vPos;
                void main() {
                    vPos = position;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                varying vec3 vPos;
                uniform float uTime;
                float hash(vec3 p){ return fract(sin(dot(p, vec3(127.1, 311.7, 74.7)))*43758.5453); }
                float noise(vec3 p){
                    vec3 i = floor(p);
                    vec3 f = fract(p);
                    f = f*f*(3.0-2.0*f);
                    float n = mix(
                        mix(mix(hash(i+vec3(0,0,0)), hash(i+vec3(1,0,0)), f.x),
                            mix(hash(i+vec3(0,1,0)), hash(i+vec3(1,1,0)), f.x), f.y),
                        mix(mix(hash(i+vec3(0,0,1)), hash(i+vec3(1,0,1)), f.x),
                            mix(hash(i+vec3(0,1,1)), hash(i+vec3(1,1,1)), f.x), f.y), f.z);
                    return n;
                }
                float fbm(vec3 p){
                    float v = 0.0; float a = 0.5;
                    for(int i=0; i<5; i++){ v += a * noise(p); p *= 2.05; a *= 0.5; }
                    return v;
                }
                void main() {
                    vec3 dir = normalize(vPos);
                    float n = fbm(dir * 2.2 + vec3(uTime * 0.012));
                    n = pow(n, 1.6);
                    vec3 paleSky = vec3(0.958, 0.986, 1.0);
                    vec3 cyanWash = vec3(0.70, 0.895, 0.975);
                    vec3 col = mix(paleSky, cyanWash, n);
                    gl_FragColor = vec4(col, 1.0);
                }
            `,
        });
        const nebula = new THREE.Mesh(nebulaGeo, nebulaMat);
        scene.add(nebula);

        // --- Lights ---
        scene.add(new THREE.AmbientLight(0xffffff, 0.75));
        const keyLight = new THREE.PointLight(0xffffff, 6.5, 50, 1.6);
        keyLight.position.set(4, 5, 8);
        scene.add(keyLight);
        const rimLight = new THREE.PointLight(0x00aeef, 4.2, 32, 2);
        rimLight.position.set(-7, -3, -4);
        scene.add(rimLight);
        const accentLight = new THREE.PointLight(0x66d5ff, 2.6, 22, 2);
        accentLight.position.set(0, 0, 5);
        scene.add(accentLight);

        // --- Materials ---
        // Solid deep-teal atoms — dark enough to read against the pale backdrop
        const atomGlassMat = new THREE.MeshPhysicalMaterial({
            color: 0x0a5e8c,
            emissive: 0x0994d1,
            emissiveIntensity: 0.5,
            metalness: 0.08,
            roughness: 0.18,
            clearcoat: 1.0,
            clearcoatRoughness: 0.03,
            transmission: 0.15,
            ior: 1.5,
            thickness: 0.6,
        });
        const atomCentralMat = new THREE.MeshPhysicalMaterial({
            color: 0x0a6690,
            emissive: 0x00aeef,
            emissiveIntensity: 0.42,
            metalness: 0.1,
            roughness: 0.15,
            clearcoat: 1.0,
            clearcoatRoughness: 0.03,
            transmission: 0.1,
            ior: 1.5,
            thickness: 0.8,
        });
        // Atom halo layers
        const haloInnerMat = new THREE.MeshBasicMaterial({
            color: 0x00aeef, transparent: true, opacity: 0.24,
            blending: THREE.NormalBlending, depthWrite: false,
        });
        const haloMidMat = new THREE.MeshBasicMaterial({
            color: 0x00aeef, transparent: true, opacity: 0.14,
            blending: THREE.NormalBlending, depthWrite: false,
        });
        const haloOuterMat = new THREE.MeshBasicMaterial({
            color: 0x0e7490, transparent: true, opacity: 0.07,
            blending: THREE.NormalBlending, depthWrite: false,
        });
        // Thick glassy bonds (intra-unit)
        const bondThickMat = new THREE.MeshPhysicalMaterial({
            color: 0x0a76a3,
            emissive: 0x0994d1,
            emissiveIntensity: 0.55,
            metalness: 0.25,
            roughness: 0.15,
            clearcoat: 0.85,
            clearcoatRoughness: 0.08,
            transparent: true,
            opacity: 0.95,
            transmission: 0.1,
            ior: 1.45,
            thickness: 0.3,
        });
        // Thin filament bonds (inter-unit)
        const bondThinMat = new THREE.MeshPhysicalMaterial({
            color: 0x1c8ec2,
            emissive: 0x0994d1,
            emissiveIntensity: 0.45,
            metalness: 0.3,
            roughness: 0.2,
            transparent: true,
            opacity: 0.8,
        });
        // Bond glow sleeves
        const bondGlowMat = new THREE.MeshBasicMaterial({
            color: 0x00aeef, transparent: true, opacity: 0.13,
            blending: THREE.NormalBlending, depthWrite: false,
        });

        // --- Reusable geometries ---
        const sphereHi = new THREE.SphereGeometry(1, 96, 96);
        const sphereLo = new THREE.SphereGeometry(1, 32, 32);

        // --- Build the cluster ---
        const cluster = new THREE.Group();
        scene.add(cluster);

        const rand = (a, b) => a + Math.random() * (b - a);

        // Tetrahedral unit vectors (4 vertices of a regular tetrahedron centered at origin)
        const TETRAHEDRON = [
            new THREE.Vector3(1, 1, 1),
            new THREE.Vector3(1, -1, -1),
            new THREE.Vector3(-1, 1, -1),
            new THREE.Vector3(-1, -1, 1),
        ].map((v) => v.normalize());

        // Distribute unit centers in 3D space — moderate density, mostly forward of camera
        const unitCenters = [];
        const UNIT_COUNT = 9;
        for (let i = 0; i < UNIT_COUNT; i++) {
            // Spherical-shell-ish distribution biased forward
            const r = rand(2.4, 4.8);
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(rand(-1, 1));
            const x = r * Math.sin(phi) * Math.cos(theta) * 0.9;
            const y = r * Math.sin(phi) * Math.sin(theta) * 0.85;
            const z = r * Math.cos(phi) * 0.9;
            unitCenters.push(new THREE.Vector3(x, y, z));
        }
        // Add 2 more units biased to the "front" (closer to camera) for DOF interest
        for (let i = 0; i < 2; i++) {
            unitCenters.push(new THREE.Vector3(rand(-1.5, 2.0), rand(-1.0, 1.5), rand(3.0, 4.5)));
        }

        // For inter-unit filament generation
        const peripheralAtoms = []; // collect peripheral atom positions

        unitCenters.forEach((center, ui) => {
            const bondLen = rand(0.85, 1.15); // distance from central to peripheral
            const centralR = rand(0.42, 0.58);
            const peripheralR = rand(0.22, 0.32);

            // Random rotation for this unit so each looks unique
            const unitRot = new THREE.Quaternion().setFromEuler(
                new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI)
            );

            // Central atom
            const central = new THREE.Mesh(sphereHi, atomCentralMat);
            central.position.copy(center);
            central.scale.setScalar(centralR);
            cluster.add(central);

            // Halo around central
            const haloC = new THREE.Mesh(sphereLo, haloInnerMat);
            haloC.position.copy(center);
            haloC.scale.setScalar(centralR * 1.8);
            cluster.add(haloC);
            const haloMidC = new THREE.Mesh(sphereLo, haloMidMat);
            haloMidC.position.copy(center);
            haloMidC.scale.setScalar(centralR * 3.0);
            cluster.add(haloMidC);
            const haloOutC = new THREE.Mesh(sphereLo, haloOuterMat);
            haloOutC.position.copy(center);
            haloOutC.scale.setScalar(centralR * 5.0);
            cluster.add(haloOutC);

            // Place 4 peripheral atoms in tetrahedral arrangement
            TETRAHEDRON.forEach((unitVec) => {
                const dir = unitVec.clone().applyQuaternion(unitRot);
                const peripheralPos = center.clone().add(dir.clone().multiplyScalar(bondLen));

                // Peripheral atom
                const periph = new THREE.Mesh(sphereHi, atomGlassMat);
                periph.position.copy(peripheralPos);
                periph.scale.setScalar(peripheralR);
                cluster.add(periph);

                // Halos
                const h1 = new THREE.Mesh(sphereLo, haloInnerMat);
                h1.position.copy(peripheralPos);
                h1.scale.setScalar(peripheralR * 1.7);
                cluster.add(h1);
                const h2 = new THREE.Mesh(sphereLo, haloMidMat);
                h2.position.copy(peripheralPos);
                h2.scale.setScalar(peripheralR * 2.8);
                cluster.add(h2);

                peripheralAtoms.push({ pos: peripheralPos, unit: ui });

                // Thick tapered bond (use Cylinder)
                const dist = bondLen;
                const bondGeo = new THREE.CylinderGeometry(0.06, 0.11, dist, 24, 1, true);
                const bond = new THREE.Mesh(bondGeo, bondThickMat);
                const mid = center.clone().add(peripheralPos).multiplyScalar(0.5);
                bond.position.copy(mid);
                bond.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
                cluster.add(bond);

                // Bond glow sleeve
                const sleeveGeo = new THREE.CylinderGeometry(0.18, 0.22, dist * 0.96, 16, 1, true);
                const sleeve = new THREE.Mesh(sleeveGeo, bondGlowMat);
                sleeve.position.copy(mid);
                sleeve.quaternion.copy(bond.quaternion);
                cluster.add(sleeve);
            });
        });

        // Inter-unit thin filament bonds — connect nearest peripheral pairs from different units
        const maxFilamentLen = 1.9;
        const filaments = [];
        for (let i = 0; i < peripheralAtoms.length; i++) {
            for (let j = i + 1; j < peripheralAtoms.length; j++) {
                if (peripheralAtoms[i].unit === peripheralAtoms[j].unit) continue;
                const d = peripheralAtoms[i].pos.distanceTo(peripheralAtoms[j].pos);
                if (d < maxFilamentLen) {
                    filaments.push({ a: peripheralAtoms[i].pos, b: peripheralAtoms[j].pos, len: d });
                }
            }
        }
        // Cap filament count for performance
        filaments.sort((a, b) => a.len - b.len);
        filaments.slice(0, 24).forEach(({ a, b, len }) => {
            const dir = b.clone().sub(a);
            const mid = a.clone().add(b).multiplyScalar(0.5);
            const geo = new THREE.CylinderGeometry(0.025, 0.025, len, 12, 1, true);
            const fil = new THREE.Mesh(geo, bondThinMat);
            fil.position.copy(mid);
            fil.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
            cluster.add(fil);

            const sleeveGeo = new THREE.CylinderGeometry(0.07, 0.07, len * 0.94, 10, 1, true);
            const sleeve = new THREE.Mesh(sleeveGeo, bondGlowMat);
            sleeve.position.copy(mid);
            sleeve.quaternion.copy(fil.quaternion);
            cluster.add(sleeve);
        });

        // Anchor cluster toward the top-right of viewport
        cluster.position.x = 1.6;
        cluster.position.y = 1.0;
        cluster.scale.setScalar(0.735);

        // --- Distant dust ---
        const dustCount = 480;
        const dustGeo = new THREE.BufferGeometry();
        const dustPos = new Float32Array(dustCount * 3);
        for (let i = 0; i < dustCount; i++) {
            dustPos[i * 3] = rand(-24, 24);
            dustPos[i * 3 + 1] = rand(-14, 14);
            dustPos[i * 3 + 2] = rand(-22, 4);
        }
        dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
        const dustMat = new THREE.PointsMaterial({
            color: 0x1b7fb0,
            size: 0.04,
            sizeAttenuation: true,
            transparent: true,
            opacity: 0.55,
            blending: THREE.NormalBlending,
            depthWrite: false,
        });
        const dust = new THREE.Points(dustGeo, dustMat);
        scene.add(dust);

        // --- Pointer / drag interaction ---
        const target = { x: 0, y: 0 };
        const current = { x: 0, y: 0 };
        let dragging = false;
        const last = { x: 0, y: 0 };
        // Diagonal rotation axis (not pure Y) — gives the tilted rotation feel
        const rotAxis = new THREE.Vector3(0.18, 1, 0.12).normalize();

        const onMove = (e) => {
            const rect = renderer.domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
            target.y = x * 0.4;
            target.x = -y * 0.25;
            if (dragging) {
                const dx = e.clientX - last.x;
                const dy = e.clientY - last.y;
                cluster.rotation.y += dx * 0.006;
                cluster.rotation.x += dy * 0.006;
                last.x = e.clientX;
                last.y = e.clientY;
            }
        };
        const onDown = (e) => {
            dragging = true;
            last.x = e.clientX;
            last.y = e.clientY;
            renderer.domElement.style.cursor = "grabbing";
        };
        const onUp = () => {
            dragging = false;
            renderer.domElement.style.cursor = "grab";
        };
        renderer.domElement.style.cursor = "grab";
        window.addEventListener("pointermove", onMove);
        renderer.domElement.addEventListener("pointerdown", onDown);
        window.addEventListener("pointerup", onUp);

        const onResize = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            renderer.setSize(w, h);
            fitCamera();
        };
        window.addEventListener("resize", onResize);

        let frame;
        const clock = new THREE.Clock();
        const animate = () => {
            const t = clock.getElapsedTime();
            const delta = clock.getDelta();

            // Smooth diagonal-axis rotation
            if (!dragging) {
                cluster.rotateOnAxis(rotAxis, delta * 0.35);
            }

            // Pointer parallax around the top-right anchor
            current.x += (target.x - current.x) * 0.04;
            current.y += (target.y - current.y) * 0.04;
            cluster.position.x = 1.6 + current.y * 0.2;
            cluster.position.y = 1.0 + current.x * 0.2;

            // Subtle key-light flicker
            keyLight.intensity = 8.0 + Math.sin(t * 1.3) * 0.6;

            nebulaMat.uniforms.uTime.value = t;
            dust.rotation.y += delta * 0.018;
            dust.rotation.x += delta * 0.006;

            renderer.render(scene, camera);
            frame = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("resize", onResize);
            renderer.domElement.removeEventListener("pointerdown", onDown);
            if (renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            renderer.dispose();
            nebulaGeo.dispose();
            nebulaMat.dispose();
            sphereHi.dispose();
            sphereLo.dispose();
            dustGeo.dispose();
            dustMat.dispose();
            atomGlassMat.dispose();
            atomCentralMat.dispose();
            haloInnerMat.dispose();
            haloMidMat.dispose();
            haloOuterMat.dispose();
            bondThickMat.dispose();
            bondThinMat.dispose();
            bondGlowMat.dispose();
            cluster.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
            });
        };
    }, []);

    return <div ref={mountRef} className="absolute inset-0" data-testid="hero-molecule-3d" />;
}
